import { ERROR_CODES } from "../../shared/constants";
import { supabase } from "../../../supabase";

// fetch static data

/**
 * Fetches the supplier contact types from the database.
 *
 * @returns {Promise<{error: number, data: Array<object>}>}
 * A promise resolving to an object containing the error code and the
 * fetched data. If the data fetch fails, error is set to
 * ERROR_CODES.DB_DATA_FETCH_FAILED and data is an empty array. If
 * successful, error is 0 and data is an array of supplier contact types.
 */
export async function fetchSupplierContactTypes() {
  const { error, data } = await supabase
    .schema(process.env.REACT_APP_SB_SCHEMA)
    .from("refs_supplier_contact_type")
    .select()
    .order("id", { ascending: true });

  if (error) {
    console.error(error);
    return { error: ERROR_CODES.DB_DATA_FETCH_FAILED, data: [] };
  }
  return { error: 0, data };
}

/**
 * Fetches the supplier statuses from the database.
 *
 * @returns {Promise<{error: number, data: Array<object>}>}
 * A promise resolving to an object containing the error code and the
 * fetched data. If the data fetch fails, error is set to
 * ERROR_CODES.DB_DATA_FETCH_FAILED and data is an empty array. If
 * successful, error is 0 and data is an array of supplier statuses.
 */
export async function fetchSupplierStatusses() {
  const { error, data } = await supabase
    .schema(process.env.REACT_APP_SB_SCHEMA)
    .from("refs_supplier_status")
    .select();

  if (error) {
    console.error(error);
    return { error: ERROR_CODES.DB_DATA_FETCH_FAILED, data: [] };
  }
  return { error: 0, data };
}

/**
 * Fetches all admin users from the "users_master" table in the database.
 *
 * @returns {Promise<{error: number, data: Array<object>}>}
 * A promise resolving to an object containing the error code and the
 * fetched data. If the data fetch fails, error is set to
 * ERROR_CODES.DB_DATA_FETCH_FAILED and data is an empty array. If
 * successful, error is 0 and data is an array of admin users.
 */
export async function fetchAdminUsers() {
  const { error, data } = await supabase
    .schema(process.env.REACT_APP_SB_SCHEMA)
    .from("users_master")
    .select()
    .eq("is_admin", true);
  if (error) {
    console.error(error);
    return { error: ERROR_CODES.DB_DATA_FETCH_FAILED, data: [] };
  }
  return { error: 0, data };
}

// fetch form data

/**
 * Fetches a supplier's form data from the database.
 *
 * @param {number} supplierId - The ID of the supplier to fetch.
 * @returns {Promise<{error: number, data: object}>}
 * A promise resolving to an object containing the error code and the
 * fetched data. If the data fetch fails, error is set to
 * ERROR_CODES.DB_DATA_FETCH_FAILED and data is an empty array. If
 * successful, error is 0 and data is an array containing the supplier's
 * form data.
 */
export async function fetchFormData(supplierId) {
  const { error, data } = await supabase
    .schema(process.env.REACT_APP_SB_SCHEMA)
    .from("suppliers_master")
    .select(
      "*, suppliers_contact_detail(*), suppliers_assist_email_index(*, users_master(*))"
    )
    .eq("id", supplierId)
    .eq("suppliers_assist_email_index.is_active", true);
  if (error) {
    console.error(error);
    return { error: ERROR_CODES.DB_DATA_FETCH_FAILED, data: {} };
  }
  return { error: 0, data: data[0] };
}

// data submit

/**
 * Clears all is_active flags for a given supplier ID in the suppliers_assist_email_index table.
 *
 * @param {number} supplierId - The ID of the supplier to clear.
 * @returns {Promise<{error: number, data: object}>}
 * A promise resolving to an object containing the error code and the
 * cleared data. If the operation fails, error is set to
 * ERROR_CODES.DB_INDEX_CLEAR_FAILED and data is an empty object. If
 * successful, error is 0 and data is the cleared data.
 */
export async function clearEmailIndex(supplierId) {
  const { data, error } = await supabase
    .schema(process.env.REACT_APP_SB_SCHEMA)
    .from("suppliers_assist_email_index")
    .update({ is_active: false })
    .eq("suppliers_master_id", supplierId);

  if (error) {
    console.error(error);
    return { error: ERROR_CODES.DB_INDEX_CLEAR_FAILED, data: [] };
  }

  return { data, error: 0 };
}

/**
 * Upserts supplier contact details into the suppliers_contact_detail table.
 * If a contact already exists, it updates the contact details.
 * If a contact does not exist, it creates a new entry with the provided details.
 *
 * @param {Array<object>} supplierContacts - An array of supplier contact details to upsert.
 * @returns {Promise<{data: Array<object>, error: number}>} - A promise resolving to an object containing the upserted data and an error code.
 * If the operation fails, error is set to ERROR_CODES.DB_INDEX_UPSERT_FAILED and data is an empty array. If successful, error is 0 and data contains the upserted contact details.
 */
export async function upsertSupplierContacts(supplierContacts) {
  const { data, error } = await supabase
    .schema(process.env.REACT_APP_SB_SCHEMA)
    .from("suppliers_contact_detail")
    .upsert(supplierContacts, { onConflict: "id" });

  if (error) {
    console.error(error);
    return { error: ERROR_CODES.DB_INDEX_UPSERT_FAILED, data: [] };
  }

  return { data, error: 0 };
}

/**
 * Upserts the assist admin email addresses into the suppliers_assist_email_index table.
 * If an email already exists, it updates the is_active flag.
 * If an email does not exist, it creates a new entry with the is_active flag set to true.
 *
 * @param {Array<object>} adminUsers - An array of admin users to upsert, each object should contain the email address
 * and the supplier ID.
 * @returns {Promise<{data: Array<object>, error: number}>} - A promise resolving to an object containing the upserted data and an error code.
 * If the operation fails, error is set to ERROR_CODES.DB_INDEX_UPSERT_FAILED and data is an empty array. If successful, error is 0 and data contains the upserted email addresses.
 */
export async function upsertAssistAdmins(adminUsers) {
  const { data, error } = await supabase
    .schema(process.env.REACT_APP_SB_SCHEMA)
    .from("suppliers_assist_email_index")
    .upsert(adminUsers);

  if (error) {
    console.error(error);
    return { error: ERROR_CODES.DB_INDEX_UPSERT_FAILED, data: [] };
  }

  return { data, error: 0 };
}
